// #3141: the Google access token for "Accedi con Google" comes from Google
// Identity Services (token client) instead of Firebase's popup. With sign-ups
// disabled in Firebase Auth, signInWithPopup cannot create a new account and
// exposes no token for the server; this popup uses the same OAuth client, so
// the account chooser and the consent are the ones users see today.
const GIS_SRC = "https://accounts.google.com/gsi/client";
const SCOPE = "openid email profile";

let loading = null;
// The last load attempt failed (offline, blocked by an extension or a policy).
let lastLoadFailed = false;

function oauth2() {
  return typeof window !== "undefined" ? window.google?.accounts?.oauth2 : undefined;
}

// The codes below are synthetic Firebase-style codes (`auth/...`), not raised
// by Firebase: they keep the ten sign-in callers, which already map Firebase
// codes to user messages, unchanged.
function authError(code, message = code) {
  return Object.assign(new Error(message), { code });
}

/** Loads the GIS script once; resolves when it is there. */
export function loadGoogleIdentityServices() {
  if (oauth2()) return Promise.resolve();
  if (!loading) {
    lastLoadFailed = false;
    loading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        loading = null;
        lastLoadFailed = true;
        script.remove();
        reject(authError("auth/network-request-failed", "Accesso con Google non disponibile. Riprova."));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
}

// GIS error types → the Firebase codes the sign-in callers already handle.
const POPUP_ERRORS = {
  popup_closed: "auth/popup-closed-by-user",
  popup_failed_to_open: "auth/popup-blocked",
};

/**
 * Opens Google's account chooser and resolves with an access token. Call it
 * straight from the click, before any await: the popup needs the user's
 * gesture, so the script must already be loaded. Every login UI calls
 * loadGoogleIdentityServices() when it becomes visible, which is a click
 * earlier than the Google button.
 */
export function requestGoogleAccessToken(clientId) {
  // Provider not enabled in this environment (no OAuth client configured).
  if (!clientId) {
    return Promise.reject(
      authError("auth/operation-not-allowed", "Accesso con Google non disponibile in questo ambiente.")
    );
  }
  const client = oauth2();
  if (!client) {
    const loadFailed = lastLoadFailed;
    loadGoogleIdentityServices().catch(() => {});
    return Promise.reject(
      loadFailed
        ? authError(
            "auth/network-request-failed",
            "Accesso con Google non disponibile. Controlla la connessione o eventuali blocchi del browser e riprova."
          )
        : authError("auth/popup-blocked", "Accesso con Google non ancora pronto. Riprova tra un istante.")
    );
  }
  return new Promise((resolve, reject) => {
    try {
      client
        .initTokenClient({
          client_id: clientId,
          scope: SCOPE,
          callback: (response) => {
            if (response?.access_token) resolve(response.access_token);
            else if (response?.error === "access_denied") reject(authError("auth/popup-closed-by-user"));
            else reject(authError("auth/internal-error", "Accesso con Google non riuscito. Riprova."));
          },
          error_callback: (error) =>
            reject(
              authError(
                POPUP_ERRORS[error?.type] || "auth/internal-error",
                error?.type === "popup_failed_to_open"
                  ? "Il browser ha bloccato la finestra di Google. Consenti i popup e riprova."
                  : "Accesso con Google non riuscito. Riprova."
              )
            ),
        })
        .requestAccessToken();
    } catch {
      reject(authError("auth/internal-error", "Accesso con Google non riuscito. Riprova."));
    }
  });
}
